/*
 * improvemint-cart-fulfillment.js — the cart's fulfilment banner: keeping it in
 * step with the shopper's latest choice, and downgrading a pickup cart that
 * cannot be picked up.
 *
 * GP, 2026-09-24, reviewing the live preview:
 *
 *   "In cart / mini-cart do we display to user what shipping option was
 *    selected? Goal here is to mitigate risk if many items are mix shipping, to
 *    pick one, latest option picked. And if pickup in store is not available for
 *    all items in cart, then show an alert message and show that items were
 *    switched to ground shipping instead."
 *
 *   "launching a mixed shipping on a non plus plan may not be doable so keep
 *    that in mind."
 *
 * ── THE MODEL: ONE FULFILMENT PER CART, LAST CHOICE WINS ──────────────────
 * That second sentence is the design. The cart carries exactly ONE fulfilment as
 * cart attributes (docs/COMMERCE-PDP-PICKER.md §6) because Shopify checkout
 * offers one delivery choice and editing checkout needs Plus, and
 * `deriveFulfillmentChoice` in the commerce app assumes it. True per-item mixed
 * shipping is not something this plan can offer, so nothing here tries: the most
 * recent PDP choice governs the whole cart, and this file's job is to make that
 * VISIBLE rather than to reconcile anything.
 *
 * ── WHAT IS LIQUID AND WHAT IS THIS FILE ──────────────────────────────────
 * The three states — store pickup, home delivery, ground shipping — are rendered
 * server-side by snippets/improvemint-cart-fulfillment.liquid straight from
 * `cart.attributes`. They are correct with no JS, correct before this file runs,
 * and re-rendered by the theme on every quantity change. This file adds exactly
 * four things on top:
 *
 *   1. a repaint when a PDP write moves the choice while the cart markup on the
 *      page is already rendered (the mini-cart lives on every page),
 *   2. the pickup-eligibility check, and the downgrade message, which need
 *      /apps/bestbuy/options and so cannot be Liquid — and, since 2026-10-17, the
 *      one case the downgrade must NOT touch: an item that cannot ship either.
 *      See "never downgrade a truck-only item" at applyVerdict(),
 *   3. clearing the choice off an EMPTY cart (2026-09-25) — Liquid can see that
 *      the cart is empty and still carries one, but it cannot write. See
 *      clearEmptyCart(), and
 *   4. re-reading a home-delivery window that went stale (2026-10-01) — a date
 *      Liquid keeps printing after it has passed. See refreshDelivery().
 *
 * It also SHARES OUT its ground-shipping write (2026-09-26) as
 * `window.imCartFulfillment.switchToShipping`, so that the checkout re-check in
 * assets/improvemint-cart-recheck.js does not become a fourth copy of the
 * eleven-key contract. That re-check is a separate mechanism on the same markup:
 * it fires on the CHECKOUT CLICK, not on a render, and it writes into its own
 * [data-im-cart-recheck] slot, never into the downgrade notice below.
 *
 * ── HOW IT AVOIDS SPENDING BEST BUY'S RATE LIMIT ──────────────────────────
 * The batch endpoint costs one upstream Commerce call PER SKU on a cold cache,
 * serialised at 2 req/s in a bucket SHARED with live order placement (F13), and
 * Best Buy named availability-on-every-view as misuse (F5). So, in order:
 *
 *   - Nothing is called unless the cart is on STORE PICKUP. A shipping or
 *     home-delivery cart is never checked: there is no downgrade to find. (A
 *     home-delivery cart has its own, separately budgeted re-read — once a day
 *     at most, through /apps/bestbuy/delivery; see refreshDelivery().)
 *   - Nothing is called unless a cart banner is actually ON SCREEN. An
 *     IntersectionObserver holds the check until the shopper looks at the cart
 *     page or opens the drawer, so browsing ten PDPs with a pickup cart in the
 *     background costs nothing.
 *   - ONE request for the whole cart (`?skus=a,b,c`), chunked at 25 because the
 *     server slices silently past that, and chunks run in SERIES — firing them
 *     together is how you rate-limit checkout.
 *   - The VERDICT is cached in sessionStorage for 10 minutes against the sorted
 *     SKU set (the spec's own sessionStorage guidance), so re-renders, quantity
 *     changes that do not change the set, and re-opening the drawer are free.
 *     The server also caches 60 s per SKU across all shoppers.
 *   - A per-page guard (`settledKey`) means two banners on the cart page — the
 *     page's and the global drawer's — share one answer and one cart write.
 *
 * An UNKNOWN verdict is deliberately NOT cached. Caching it would keep
 * downgrading for ten minutes on the strength of one failed request; not caching
 * it means the next look asks again.
 *
 * ── FAIL SAFE ─────────────────────────────────────────────────────────────
 * If the check cannot be made, the cart goes to ground shipping — unless it holds
 * an item tagged `bb-delivery:home` (2026-10-17), which ground shipping cannot
 * carry either; then nothing is written and the item is named instead (see
 * applyVerdict). A pickup we
 * cannot honour is the expensive failure: placement refuses such a SKU
 * (NO_AGENT_PICKUP_SKU / a store with no stock) AFTER the shopper has paid,
 * whereas an unnecessary downgrade costs them one re-pick on the product page
 * and is stated plainly on screen. The server agrees with this reading — its own
 * degraded path answers `pickup: false` for every SKU rather than guessing.
 *
 * "Cannot be made" covers a rejected fetch, a non-2xx, a body with `error` set
 * (the server reached us but not Best Buy, so its `pickup: false` means "we do
 * not know" and must NOT be reported as a named product), and any SKU missing
 * from the answer.
 *
 * ── WHAT IT KNOWS AND WHAT IT DOES NOT ────────────────────────────────────
 * `cartEligibility` answers "is this SKU pickup-eligible at all" — it takes no
 * ZIP and no store, so it cannot answer "at the store this shopper chose". The
 * copy therefore says pickup is not available for the item, which is what we
 * actually know, and never names the store as the reason. Narrowing it to the
 * chosen store would need a per-SKU call WITH the ZIP, which is the per-view
 * traffic Best Buy asked us not to send.
 *
 * No dependencies. Idempotent.
 */
(function () {
  'use strict';
  if (window.__imCartFulfillmentInit) return;
  window.__imCartFulfillmentInit = true;

  var ENDPOINT = '/apps/bestbuy/options';
  var BATCH_MAX = 25;                        // the server slices at 25 silently
  // `2` since 2026-10-17: verdicts now carry `home`, and one cached by the old
  // script (up to 10 min old) would lack it and downgrade a truck-only item.
  var ELIG_PREFIX = 'im_bb_cart_elig2:';
  var ELIG_TTL_MS = 10 * 60 * 1000;          // the spec's sessionStorage guidance
  var MEMO_KEY = 'im_bb_cart_downgrade';
  var NAMES_SHOWN = 3;                       // then "and N more items"

  /* ---------------------------------------------------------------- storage */

  /* Wrapped, like both pickers: Safari in private mode throws on write, and a
   * shopper with site data blocked must lose the cache, not the cart. */
  function read(store, key) {
    try { return store.getItem(key); } catch (e) { return null; }
  }
  function write(store, key, value) {
    try { store.setItem(key, value); } catch (e) { /* non-fatal */ }
  }
  function drop(store, key) {
    try { store.removeItem(key); } catch (e) { /* non-fatal */ }
  }

  function eligGet(key) {
    try {
      var raw = JSON.parse(read(sessionStorage, ELIG_PREFIX + key) || 'null');
      if (!raw || Date.now() > raw.e) return null;
      return raw.v;
    } catch (e) { return null; }
  }
  function eligSet(key, verdict) {
    write(sessionStorage, ELIG_PREFIX + key, JSON.stringify({ e: Date.now() + ELIG_TTL_MS, v: verdict }));
  }

  /*
   * The downgrade we last raised, remembered for the session.
   *
   * Our own cart write is followed by a page load sooner or later, and by then
   * the cart is ground shipping and Liquid renders no reason for it. Without this
   * the message would be visible only in the seconds between the answer landing
   * and the shopper navigating — i.e. exactly the alert GP asked for, missed.
   *
   * Keyed by the SKU set it was raised for, so any add or remove retires it
   * rather than letting it describe a cart it is no longer about.
   */
  function memoGet() {
    try { return JSON.parse(read(sessionStorage, MEMO_KEY) || 'null'); } catch (e) { return null; }
  }
  function memoSet(memo) { write(sessionStorage, MEMO_KEY, JSON.stringify(memo)); }
  function memoClear() { drop(sessionStorage, MEMO_KEY); }

  /* --------------------------------------------------------------- elements */

  function banners() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-im-cart-fulfillment-banner]'));
  }

  function label(banner, name, fallback) {
    return (banner && banner.getAttribute('data-label-' + name)) || fallback;
  }

  /*
   * The cart's Best Buy lines, as { sku, title, home, url }, from the index Liquid
   * rendered inside the banner. Elements rather than a JSON attribute: Liquid has
   * no way to build a JSON array of strings without hand-rolling the quoting, and
   * attribute-per-fact needs no escaping rules beyond `| escape`.
   *
   * `home` (2026-10-01) is the product's `bb-delivery:home` tag — Best Buy only
   * brings it on its own truck. refreshDelivery() reads it to choose which SKU to
   * ask first, and since 2026-10-17 the downgrade reads it too: an item that only
   * comes by truck is never switched to ground shipping. *This said "it is false
   * on every line until bestbuy-sync publishes the tag". It is published: the
   * Fisher & Paykel dishwasher used for QA (sku 6196700) carries it, read from
   * its product JSON on 2026-10-17.*
   *
   * `url` (2026-10-17) is the line's own product page, `item.url` — the notice
   * that names a truck-only item links to it, because that page is where a
   * delivery date is chosen.
   */
  function lines(banner) {
    return Array.prototype.slice
      .call(banner.querySelectorAll('[data-im-cart-sku-title]'))
      .map(function (el) {
        return {
          sku: el.getAttribute('data-im-cart-sku') || '',
          title: el.getAttribute('data-im-cart-sku-title') || '',
          home: el.getAttribute('data-im-cart-sku-home') === '1',
          url: el.getAttribute('data-im-cart-sku-url') || ''
        };
      })
      .filter(function (l) { return !!l.sku; });
  }

  /* The cart's identity for caching: order must not matter, duplicates must not. */
  function skuKey(items) {
    var seen = {};
    var out = [];
    items.forEach(function (i) {
      if (seen[i.sku]) return;
      seen[i.sku] = true;
      out.push(i.sku);
    });
    return out.sort().join(',');
  }

  /* --------------------------------------------------------------- painting */

  var STATES = ['pickup', 'home-delivery', 'shipping'];

  /*
   * Repaint one banner into a state. Text and attributes only — every element it
   * touches is in the DOM already, in every state, so nothing is injected and
   * nothing can be orphaned by a later write.
   *
   * `value` is the variable half: the store name for pickup, the window label for
   * home delivery, nothing for shipping. It goes in a <strong> built here rather
   * than via innerHTML, so a store name with an ampersand in it cannot become
   * markup.
   */
  function paintState(banner, state, value) {
    if (STATES.indexOf(state) === -1) state = 'shipping';
    banner.setAttribute('data-state', state);

    var icon = banner.querySelector('[data-im-cart-fulfillment-icon]');
    if (icon) icon.hidden = state === 'shipping';

    var labelEl = banner.querySelector('[data-im-cart-fulfillment-label]');
    if (labelEl) {
      var fallbackLabel = state === 'pickup'
        ? 'Store pickup'
        : state === 'home-delivery' ? 'Home delivery' : 'Ground shipping';
      labelEl.textContent = label(banner, state, fallbackLabel);
      if (value) {
        labelEl.appendChild(document.createTextNode(' · '));
        var strong = document.createElement('strong');
        strong.textContent = value;
        labelEl.appendChild(strong);
      }
    }

    /*
     * The detail comes from `data-label-<state>-detail`, which Liquid renders
     * already composed for THIS cart (2026-10-01): a pickup or home-delivery cart
     * that also holds items shipping to the address carries "… Other items ship
     * to your address." there. The fallbacks below are the plain sentences, kept
     * identical to the Liquid defaults, for a banner with no label at all.
     */
    var detailEl = banner.querySelector('[data-im-cart-fulfillment-detail]');
    if (detailEl) {
      var fallbackDetail = state === 'pickup'
        ? 'Every item in your cart is set for store pickup.'
        : state === 'home-delivery'
          ? 'Confirmed when your order is placed. An adult must be home to sign for the delivery.'
          : 'Every item in your cart ships to your address.';
      detailEl.textContent = label(banner, state + '-detail', fallbackDetail);
    }
  }

  /* Up to three names, then a count. "A", "A and B", "A, B and C", "A, B, C and 2 more items". */
  function nameList(names) {
    var shown = names.filter(Boolean).slice(0, NAMES_SHOWN);
    var rest = names.filter(Boolean).length - shown.length;
    if (rest > 0) return shown.join(', ') + ' and ' + rest + ' more item' + (rest === 1 ? '' : 's');
    if (shown.length <= 1) return shown[0] || '';
    return shown.slice(0, -1).join(', ') + ' and ' + shown[shown.length - 1];
  }

  /*
   * The downgrade message. Two sentences, both needed:
   *
   *   what changed   naming the items, because "your cart was switched" without
   *                  saying which product caused it is the kind of notice a
   *                  shopper reads as a glitch.
   *   why it applies to everything, because the cart has ONE delivery method and
   *                  that is the part nobody would guess.
   *
   * `role="status"` is on the slot in Liquid, so a screen reader hears this
   * arrive — the cart is already rendered by the time the answer lands.
   */
  function paintNotice(banner, memo) {
    var slot = banner.querySelector('[data-im-cart-fulfillment-notice]');
    if (!slot) return;
    slot.textContent = '';
    if (!memo) { slot.hidden = true; return; }

    var named = !memo.unknown && memo.names && memo.names.filter(Boolean).length;
    var what = document.createElement('p');
    what.className = 'im-cart-fulfil__notice-line';
    what.textContent = named
      ? label(
          banner,
          'downgrade',
          'Store pickup isn’t available for {items}, so your cart has been switched to ground shipping.'
        ).replace('{items}', nameList(memo.names))
      : label(
          banner,
          'downgrade-unknown',
          'We couldn’t confirm store pickup for every item in your cart, so it has been switched to ground shipping.'
        );
    slot.appendChild(what);

    var why = document.createElement('p');
    why.className = 'im-cart-fulfil__notice-line im-cart-fulfil__notice-line--sub';
    why.textContent = label(
      banner,
      'one-method',
      'Your cart uses one delivery method for every item — the most recent choice applies to the whole cart.'
    );
    slot.appendChild(why);

    slot.hidden = false;
  }

  /*
   * The notice for a pickup cart holding an item that only comes by Best Buy's
   * truck (2026-10-17) — said INSTEAD of switching the cart to ground shipping,
   * which cannot carry that item either. See applyVerdict().
   *
   * The item names are LINKS to their product pages, because that is where a
   * delivery date is chosen and the sentence tells the shopper to go there.
   * Built from nodes, never innerHTML, so a product title cannot become markup.
   * The second line is the same "one delivery method" sentence the downgrade
   * uses: it is why an appliance came to be sitting in a pickup cart at all.
   */
  var HOME_NOTICE = '{items} comes by Best Buy home delivery — open its page and choose a delivery date.';

  function itemNode(item) {
    // Only a same-site path: item.url is Liquid's `/products/<handle>?variant=…`.
    if (!/^\/(?!\/)/.test(item.url || '')) return document.createTextNode(item.title);
    var a = document.createElement('a');
    a.className = 'im-cart-fulfil__notice-link';
    a.href = item.url;
    a.textContent = item.title;
    return a;
  }

  /* nameList(), with each name a link: "A", "A and B", "A, B and C", "A, B, C and 2 more items". */
  function appendItems(parent, items) {
    var named = items.filter(function (i) { return i && i.title; });
    var shown = named.slice(0, NAMES_SHOWN);
    var rest = named.length - shown.length;
    shown.forEach(function (item, i) {
      if (i > 0) parent.appendChild(document.createTextNode(i === shown.length - 1 && rest <= 0 ? ' and ' : ', '));
      parent.appendChild(itemNode(item));
    });
    if (rest > 0) parent.appendChild(document.createTextNode(' and ' + rest + ' more item' + (rest === 1 ? '' : 's')));
  }

  function paintHomeNotice(banner, verdict) {
    var slot = banner.querySelector('[data-im-cart-fulfillment-notice]');
    if (!slot) return;
    slot.textContent = '';

    var what = document.createElement('p');
    what.className = 'im-cart-fulfil__notice-line';
    String(label(banner, 'home-notice', HOME_NOTICE)).split('{items}').forEach(function (part, i) {
      if (i > 0) appendItems(what, verdict.home || []);
      if (part) what.appendChild(document.createTextNode(part));
    });
    slot.appendChild(what);

    var why = document.createElement('p');
    why.className = 'im-cart-fulfil__notice-line im-cart-fulfil__notice-line--sub';
    why.textContent = label(
      banner,
      'one-method',
      'Your cart uses one delivery method for every item — the most recent choice applies to the whole cart.'
    );
    slot.appendChild(why);

    slot.hidden = false;
  }

  /*
   * The per-line notes, which were rendered from the choice we just replaced.
   *
   * A line still reading "Pickup at Best Buy · FRISCO TX" under a banner that now
   * says ground shipping is two answers to one question. The offending lines are
   * retitled — that is where "naming what changed" lands most precisely, right
   * under the product it is about (spec §8) — and the rest are hidden, because
   * ground shipping renders no line at all by design.
   *
   * `keepOthers` (2026-10-17) is the truck-only case, where the cart is NOT
   * switched: it stays on pickup, so the lines that can be picked up keep their
   * "Pickup at Best Buy · <store>" note and only the ones that cannot are
   * retitled.
   */
  function paintLines(memo, keepOthers) {
    var blocked = {};
    ((memo && memo.skus) || []).forEach(function (s) { blocked[String(s)] = true; });
    var any = banners()[0];
    var text = label(any, 'line-no-pickup', 'Store pickup not available for this item');

    Array.prototype.slice
      .call(document.querySelectorAll('[data-im-cart-fulfillment-line]'))
      .forEach(function (el) {
        var sku = el.getAttribute('data-im-cart-sku');
        if (sku && blocked[sku]) {
          el.textContent = text;   // drops the Best Buy mark with it, deliberately
          el.className = 'im-cart-fulfillment im-cart-fulfillment--warn';
          el.hidden = false;
          return;
        }
        if (!keepOthers) el.hidden = true;
      });
  }

  /* ------------------------------------------------------------ cart writes */

  /*
   * ALL ELEVEN `bb_*` keys, the inapplicable ones as "" — the same contract both
   * pickers follow, and for the same reason: "" is how /cart/update.js DELETES an
   * attribute, and a subset write is what leaves a cart carrying half a pickup
   * beside half a delivery. `bb_zip` is kept: it is the shopper's ZIP, not part
   * of the choice we are withdrawing. The two display-only pickup keys
   * (`bb_pickup_ready_date`, `bb_store_address`, 2026-10-17) go with the pickup.
   *
   * `bb_shipping_option` is written empty on purpose. We do not have one here
   * (it comes from a per-SKU options answer with a ZIP), and placement resolves
   * the shipping option live for a ship-to-home order anyway (G6) — so empty is
   * the honest value, not a missing one.
   */
  function writeGroundShipping() {
    var banner = banners()[0];
    var zip = (banner && banner.getAttribute('data-im-cart-zip')) || '';
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          bb_fulfillment: 'delivery',
          bb_zip: zip,
          bb_store_id: '',
          bb_store_name: '',
          bb_shipping_option: '',
          bb_delivery_date: '',
          bb_delivery_start: '',
          bb_delivery_end: '',
          bb_delivery_zip: '',
          bb_delivery_earliest: '',
          bb_delivery_services: '',
          bb_pickup_ready_date: '',
          bb_store_address: ''
        }
      })
    }).catch(function () {
      /*
       * Non-fatal, and the screen is still honest: the banner and the lines have
       * been repainted to ground shipping and the message says so, which is the
       * state the shopper must not be misled about. The cart attribute is
       * re-asserted the next time anything writes it, and placement refuses the
       * pickup independently — the backend enforces per SKU whatever the theme
       * shows.
       */
    });
  }

  /*
   * The one place the eleven-key ground-shipping write lives, shared out rather
   * than copied (2026-09-26).
   *
   * assets/improvemint-cart-recheck.js offers the shopper "Switch to shipping"
   * when the store they chose can no longer provide an item, and that is exactly
   * this write. §6 of docs/COMMERCE-PDP-PICKER.md requires all eleven bb_* keys
   * on every write, the inapplicable ones as "", and there were already three
   * copies of that list in this theme (both pickers and this file). A fourth in
   * the re-check would have been the one most likely to drift, because it is the
   * only one not sitting beside a picker that writes the same keys.
   *
   * A namespaced global rather than an event, because the caller has to know
   * when the write has SETTLED before it repaints; an event gives it no promise
   * back. Both files are `defer` and this one is loaded first
   * (layout/theme.liquid), so the hook exists before a shopper can press
   * anything.
   */
  window.imCartFulfillment = window.imCartFulfillment || {};
  window.imCartFulfillment.switchToShipping = writeGroundShipping;

  /* ------------------------------------------------- the emptied cart (2026-09-25)
   *
   * GP: "i noticed that once i selected a pickup in store, then I deleted the
   * cart and selected ground shipping, cart stays stuck on pickup in store."
   *
   * Shopify keeps cart attributes when the last line item is removed, so an
   * emptied cart still says `store-pickup` at a named store. Nothing shows it —
   * with no Best Buy line there is no banner — and the next item added inherits
   * it, which `deriveFulfillmentChoice` places as a pickup at that store. An
   * empty cart has no fulfilment; this makes the attributes say so.
   *
   * The signal is Liquid's, not this file's: snippets/improvemint-cart-fulfillment.liquid
   * renders [data-im-cart-fulfillment-stale] whenever the cart is empty AND a
   * choice is still attached. That is one test covering every route to an empty
   * cart — the cart page's Remove, a quantity taken to 0, the mini-cart,
   * /cart/clear — and because the mini-cart is global it is in the DOM on every
   * page until it is acted on, so a cart emptied in another tab is cleared by
   * the next page this one renders.
   *
   * WHAT SURVIVES: `bb_zip`. The ZIP is where the shopper is, not what they
   * chose — it is written on every write including ground shipping, no cart
   * surface renders it as a statement, and `fulfillmentProps` reads nothing at
   * all when `bb_fulfillment` is absent, so it cannot reach an order on its own.
   * Keeping it saves re-typing a ZIP; keeping anything else would be a promise
   * with nothing behind it.
   *
   * WHAT GOES: the other ten, `bb_store_id` / `bb_store_name` included — a cart
   * that still named FRISCO TX would put a store on the next cart the shopper
   * built — and `bb_delivery_zip` with them, because that one is part of the
   * home-delivery choice (it is the ZIP the window was quoted for, and placement
   * checks it against the checkout address) rather than the shopper's own.
   *
   * Once per emptying, not once per page: the latch is held only while the
   * marker is on the page, so it re-arms the moment the cart has something in it
   * again and a second emptying is cleared like the first. Without the latch the
   * write would repeat on every render, because the marker stays until the
   * server next renders the cart.
   */
  var clearedEmpty = false;

  function clearEmptyCart() {
    var marker = document.querySelector('[data-im-cart-fulfillment-stale]');
    if (!marker) { clearedEmpty = false; return false; }
    if (clearedEmpty) return true;
    clearedEmpty = true;

    // A downgrade we raised was about a cart that no longer exists.
    memoClear();

    /*
     * The PDP picker's server-rendered view of the cart, if this page has one —
     * the mini-cart is global, so an emptied cart is very often cleared from a
     * product page. improvemint-delivery.js reads these to decide whether a
     * complete home-delivery window is in the cart (cartWindow()), and
     * improvemint-pickup.js reads them before an add. Leaving them saying
     * `store-pickup` after the attributes have gone is the same divergence one
     * layer up.
     */
    var picker = document.querySelector('[data-im-fulfillment]');
    if (picker) {
      picker.setAttribute('data-im-cart-fulfillment', '');
      picker.setAttribute('data-im-cart-store-id', '');
      picker.setAttribute('data-im-cart-pickup-ready-date', '');
      picker.setAttribute('data-im-cart-delivery-date', '');
      picker.setAttribute('data-im-cart-delivery-start', '');
      picker.setAttribute('data-im-cart-delivery-end', '');
      picker.setAttribute('data-im-cart-delivery-zip', '');
      picker.setAttribute('data-im-cart-delivery-earliest', '');
    }

    // All eleven keys, as everywhere else — ten blank, and the ZIP written back
    // as itself so the payload states plainly which one is kept — plus the two
    // display-only pickup keys (2026-10-17), blank: an empty cart has no pickup.
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          bb_fulfillment: '',
          bb_zip: marker.getAttribute('data-im-cart-zip') || '',
          bb_store_id: '',
          bb_store_name: '',
          bb_shipping_option: '',
          bb_delivery_date: '',
          bb_delivery_start: '',
          bb_delivery_end: '',
          bb_delivery_zip: '',
          bb_delivery_earliest: '',
          bb_delivery_services: '',
          bb_pickup_ready_date: '',
          bb_store_address: ''
        }
      })
      /*
       * Non-fatal. The marker is re-rendered on the next cart render and the
       * next page load, so a failed clear is retried rather than lost — and
       * improvemint-pickup.js re-asserts the choice from the picker at
       * add-to-cart time, which is the other half of this fix.
       */
    }).catch(function () { /* retried by the next render */ });

    return true;
  }

  /* ------------------------------------------------------------- the check */

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  /*
   * One answer for the whole cart. Chunked at BATCH_MAX and run in SERIES; see
   * the rate-limit note at the top of this file.
   */
  function fetchEligibility(skus) {
    var chunks = [];
    for (var i = 0; i < skus.length; i += BATCH_MAX) chunks.push(skus.slice(i, i + BATCH_MAX));

    var out = { map: {}, degraded: false };
    return chunks
      .reduce(function (chain, chunk) {
        return chain.then(function () {
          return getJSON(ENDPOINT + '?skus=' + chunk.join(','))
            .then(function (data) {
              /*
               * The route answers 200 with `error` set when it could not reach
               * Best Buy, and then reports `pickup: false` for EVERY sku. That is
               * "we do not know", not "these cannot be picked up" — reading it
               * the second way would name every product in the cart as the
               * culprit. Degrade instead: the outcome is the same downgrade, with
               * the message that does not accuse anything.
               */
              if (data && data.error) { out.degraded = true; return; }
              ((data && data.items) || []).forEach(function (it) {
                out.map[String(it.sku)] = it;
              });
            })
            .catch(function () { out.degraded = true; });
        });
      }, Promise.resolve())
      .then(function () { return out; });
  }

  /*
   * `pickup !== true`, not `pickup === false`: an absent flag is not a yes, and
   * this is the direction where the safe reading is the strict one.
   *
   * `home` (2026-10-17): the items among those that cannot SHIP either — the
   * product carries `bb-delivery:home`, or the batch answer says `shipping:
   * false`. An unanswered line counts when it carries the tag: the answer is
   * missing, but the tag is Best Buy's own word that its truck is the only way
   * this product travels, so ground shipping is no way out for it.
   */
  function verdictFrom(items, answer) {
    var blocked = [];
    var home = [];
    var unknown = answer.degraded;
    items.forEach(function (item) {
      var hit = answer.map[item.sku];
      if (!hit) {
        unknown = true;
        if (item.home) home.push(item);
        return;
      }
      if (hit.pickup !== true) {
        blocked.push(item);
        if (item.home || hit.shipping === false) home.push(item);
      }
    });
    return {
      unknown: unknown,
      names: blocked.map(function (i) { return i.title; }),
      skus: blocked.map(function (i) { return i.sku; }),
      home: home.map(function (i) { return { sku: i.sku, title: i.title, url: i.url }; })
    };
  }

  /*
   * ── NEVER DOWNGRADE A TRUCK-ONLY ITEM TO GROUND SHIPPING (2026-10-17) ───────
   * The downgrade exists because shipping is the way out for an item that cannot
   * be picked up. For an item Best Buy only brings on its own truck — a
   * `bb-delivery:home` appliance, `shipping: false` — it is not a way out at all:
   * the cart reached checkout as Standard shipping and placement failed. Live,
   * 2026-10-17: a pickup cart holding the Fisher & Paykel dishwasher read "Store
   * pickup isn't available for <dishwasher>, so your cart has been switched to
   * ground shipping."
   *
   * So when any such item is in the verdict, NOTHING is written: the cart stays
   * on pickup, and the notice names the item and sends the shopper to its page to
   * choose a delivery date. The lines that cannot be picked up are retitled and
   * the rest keep their pickup note. The checkout re-check
   * (improvemint-cart-recheck.js) refuses checkout while the cart stays like this,
   * so the notice is advice and the block is the guarantee.
   *
   * Not remembered in sessionStorage the way the downgrade is: the cart is still
   * on pickup, so every render re-runs the check, and the verdict cache (and
   * `settledVerdict` on this page) answers it with no request.
   *
   * What a cart holding a truck-only item beside items that ship or are picked up
   * SHOULD do is open — GP deciding. This makes no choice for the shopper; it
   * only stops making the wrong one.
   */
  function applyVerdict(key, verdict) {
    var list = banners();
    if (!verdict.unknown && !verdict.skus.length) {
      // Every item can be picked up. Nothing to say, and nothing to remember.
      memoClear();
      list.forEach(function (b) { paintNotice(b, null); });
      return;
    }

    if (verdict.home && verdict.home.length) {
      memoClear();
      list.forEach(function (b) { paintHomeNotice(b, verdict); });
      paintLines(verdict, true);
      return;
    }

    var memo = { key: key, unknown: verdict.unknown, names: verdict.names, skus: verdict.skus };
    memoSet(memo);
    writeGroundShipping();
    list.forEach(function (b) {
      paintState(b, 'shipping', '');
      paintNotice(b, memo);
    });
    paintLines(memo);
  }

  /*
   * `settledKey` / `pendingKey` are per PAGE, not per banner: the cart page
   * renders its own banner AND the global drawer's, and both must share one
   * answer and one cart write. They are keyed by the SKU set so an add or a
   * remove re-opens the question.
   */
  var pendingKey = null;
  var settledKey = null;
  var settledVerdict = null;

  function maybeCheck() {
    var list = banners();
    if (!list.length) return;
    // The state is a property of the CART, so any banner answers for all of them.
    if (list[0].getAttribute('data-state') !== 'pickup') return;

    var items = lines(list[0]);
    if (!items.length) return;

    var key = skuKey(items);
    if (pendingKey === key) return;
    if (settledKey === key) {
      /*
       * Answered already on this page. A truck-only verdict leaves the cart ON
       * PICKUP (2026-10-17), so a quantity change re-renders a pickup banner with
       * an empty notice and lands here again — paint it back from the answer in
       * hand. No request and no write; the downgrade never reaches this, because
       * after it the cart is no longer on pickup.
       */
      if (settledVerdict && settledVerdict.home && settledVerdict.home.length) applyVerdict(key, settledVerdict);
      return;
    }

    var cached = eligGet(key);
    if (cached) { settledKey = key; settledVerdict = cached; applyVerdict(key, cached); return; }

    pendingKey = key;
    fetchEligibility(items.map(function (i) { return i.sku; })).then(function (answer) {
      pendingKey = null;
      settledKey = key;
      var verdict = verdictFrom(items, answer);
      settledVerdict = verdict;
      // An unknown verdict is never cached — see the header.
      if (!verdict.unknown) eligSet(key, verdict);
      applyVerdict(key, verdict);
    });
  }

  /*
   * Hold the check until the shopper actually looks at their cart.
   *
   * The mini-cart is rendered on EVERY page, so without this a pickup cart would
   * spend a Best Buy call on every page view while the drawer sat closed. Hyper
   * translates `.drawer__inner` fully off the viewport until `[active]`, so an
   * IntersectionObserver against the viewport is exactly the "is the shopper
   * looking at this" test, and it costs nothing while they are not.
   *
   * No IntersectionObserver (very old browsers) falls back to checking at once:
   * correctness first, and the cache still keeps it to one call per 10 minutes.
   */
  function watch(banner, again) {
    // `again` re-arms a banner whose observer already fired and disconnected —
    // the shopper re-chose pickup on a PDP, so the question is open once more on
    // the very same element. Without it the re-pick would never be checked.
    if (again) banner.__imWatched = false;
    if (banner.__imWatched) return;
    banner.__imWatched = true;
    if (!window.IntersectionObserver) { lookedAt(); return; }
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        io.disconnect();
        lookedAt();
        return;
      }
    });
    io.observe(banner);
  }

  /* The shopper is looking at the cart. Each check below decides for itself
   * whether this cart is one it is about, so calling both costs nothing. */
  function lookedAt() {
    maybeCheck();
    refreshDelivery();
  }

  /* ------------------------------------ home delivery: a window that went stale
   *
   * GP, 2026-10-01, on the live mini-cart: it read "Home delivery · Mon, Sep 28,
   * 7:00 AM – 7:00 PM · Confirmed when your order is placed." on Oct 1 — in the
   * banner and on the line. *"On add to cart when we pull up minicart or when we
   * visit the cart page, we should refresh home delivery with new date."*
   *
   * WHERE THE STALE DATE COMES FROM. The PDP writes one complete window into the
   * cart attributes when the shopper chooses Home Delivery (improvemint-delivery.js
   * commitWindow), and nothing re-reads it afterwards. Both cart surfaces are
   * Liquid rendered straight from those attributes, so every re-render prints the
   * same date however old it gets; the checkout re-check (improvemint-cart-recheck.js)
   * only ALERTS on a moved date and deliberately writes nothing. And it is not only
   * cosmetic: placement books an "as soon as" cart on the first window on or after
   * the date it carries (bestbuy-sync, GP 2026-10-19 — the date shown is the
   * promise), so a stale date that is still ahead books later than Best Buy could
   * deliver; a day the shopper picked that has passed fails intake with
   * DELIVERY_DATE_PASSED, and one Best Buy stopped offering fails with
   * DELIVERY_DATE_UNAVAILABLE (order-validation.ts, pipeline.ts). *Corrected
   * 2026-10-19: this said `bb_delivery_earliest` is display only and placement
   * reads every window as the shopper's own choice — untrue since 2026-10-01.*
   *
   * WHAT THIS DOES, when a home-delivery banner comes on screen (the drawer opens —
   * which it does straight after an add — or the cart page loads; the same
   * IntersectionObserver the pickup check uses):
   *
   *   "as soon as" (`bb_delivery_earliest` "1")  re-read the soonest window, at
   *                         most ONCE PER UTC DAY per cart, and write it back.
   *   a day the shopper picked, still ahead       left alone. It is theirs, and
   *                         placement honours it or stops and asks.
   *   a day the shopper picked, now passed        replaced with the soonest, and
   *                         marked "as soon as", because that is now what it is.
   *
   * THE DAILY CAP is `bb_delivery_checked`, the UTC date of the last successful
   * re-read, written with the window. UTC and not the store's clock because every
   * other date in this decision is UTC: the endpoint measures `earliest` from UTC
   * today, placement fails DELIVERY_DATE_PASSED against UTC today, and the checkout
   * re-check says "has passed" against UTC today. A store-local stamp would let the
   * cart and the order disagree about which day it is for four or five hours every
   * evening. It is NOT one of the eleven `bb_*` choice keys: it describes our own
   * call budget, nothing below the theme reads it, and the other writers leave it
   * alone — a stamp for today can only skip a same-day re-read of a window some
   * writer has just read fresh.
   *
   * ONE RULE FOR "EARLIEST". The request, the 60 s cache, the session's memory of
   * refusals and the choice of window all live in improvemint-delivery.js, which
   * publishes soonestWindow() for exactly this; the same function books the PDP's
   * window. Nothing here decides what "soonest" means.
   *
   * WHICH SKU. The cart says nothing about which line the window was booked for,
   * and a cart can hold an appliance beside parcel items. So the Best Buy lines are
   * asked one at a time, and the first that answers with a window wins — lines
   * whose product carries the `bb-delivery:home` tag first (2026-10-01: Best Buy's
   * truck brings those, so they are the ones with a window), then the rest, each
   * group in cart order. A cart with no tagged line is plain cart order, as
   * before. (*This said "Until bestbuy-sync publishes that tag no line carries
   * it" — the tag is published; see lines().*) A parcel SKU answers
   * `not-home-delivery`, which soonestWindow() remembers
   * for the session, so it is asked about once and then skipped for free from the
   * cart and the PDP alike. HD_MAX_ASKS bounds a cart of many parcel items; a cart
   * with two appliances gets the first one's window, exactly as the PDP books one
   * product's window, and placement intersects them at claim time either way.
   *
   * FAILURE (busy, an error, no window, the owner switch off) writes nothing and
   * leaves the attributes as they are — but a PASSED date is never shown as if it
   * were still the plan. Before any request, the banner and the lines lose the
   * date and say the re-check's own sentence, "The delivery date saved with your
   * cart has passed. A new date is confirmed when your order is placed."
   * (`cart_recheck_delivery_passed`), which stays if the re-read fails. A failure is
   * not retried for HD_RETRY_MS this session. Nothing here touches checkout.
   */
  var HD_MAX_ASKS = 4;                       // real requests per refresh, in series
  var HD_RETRY_MS = 10 * 60 * 1000;          // after a failed re-read, this session
  var HD_RETRY_KEY = 'im_bb_cart_hd_retry';
  var HD_PASSED = 'The delivery date saved with your cart has passed. A new date is confirmed when your order is placed.';

  /* Today in UTC as YYYY-MM-DD — the same day improvemint-cart-recheck.js and
   * placement use, compared as a string against Best Buy's own date shape. */
  function todayUTC() {
    var n = new Date();
    function p(x) { return (x < 10 ? '0' : '') + x; }
    return n.getUTCFullYear() + '-' + p(n.getUTCMonth() + 1) + '-' + p(n.getUTCDate());
  }

  /* The home-delivery window this cart carries, read off the banner's raw
   * attributes, or null when it carries none the banner would show. */
  function cartWindowOf(banner) {
    function attr(name) { return (banner.getAttribute(name) || '').trim(); }
    if (attr('data-im-cart-choice') !== 'home-delivery') return null;
    var win = {
      date: attr('data-im-cart-hd-date'),
      start: attr('data-im-cart-hd-start'),
      zip: attr('data-im-cart-hd-zip') || attr('data-im-cart-zip'),
      earliest: attr('data-im-cart-hd-earliest') === '1',
      checked: attr('data-im-cart-hd-checked')
    };
    if (!/^\d{4}-\d{2}-\d{2}$/.test(win.date) || !win.start) return null;
    return win;
  }

  function windowNeedsReading(win, today) {
    if (win.date < today) return true;       // passed, whoever chose it
    if (!win.earliest) return false;         // the shopper's own day, still ahead
    return win.checked !== today;            // "as soon as": once a day
  }

  function windowKey(win) {
    return [win.date, win.start, win.zip, win.earliest ? '1' : '', win.checked].join('|');
  }

  function retryHeld(key) {
    try {
      var raw = JSON.parse(read(sessionStorage, HD_RETRY_KEY) || 'null');
      return !!(raw && raw.k === key && Date.now() < raw.e);
    } catch (e) { return false; }
  }
  function holdRetry(key) {
    write(sessionStorage, HD_RETRY_KEY, JSON.stringify({ k: key, e: Date.now() + HD_RETRY_MS }));
  }

  /*
   * Take a passed date off the screen. Text only, into nodes Liquid already
   * rendered: the banner keeps "Home delivery" and loses the date, and each
   * home-delivered Best Buy line does the same, with its "Confirmed when your
   * order is placed." note replaced by the passed sentence.
   *
   * ONLY `--delivery` and `--note` lines are touched, and that is load-bearing
   * since 2026-10-01: a home-delivery cart can now hold a Best Buy line that
   * ships in a box (snippets/improvemint-cart-pickup-line.liquid renders it
   * `--ships --parcel`, reading "Ground shipping"), and another vendor's line
   * (`--ships`, "Ships to you separately"). Neither carries a date, so neither
   * has anything to take off — and retitling either "Home delivery" would be
   * the very mislabel the per-item labels exist to remove.
   */
  function paintPassedWindow() {
    var list = banners();
    var words = label(list[0], 'rc-delivery-passed', HD_PASSED);
    list.forEach(function (b) {
      paintState(b, 'home-delivery', '');
      var detail = b.querySelector('[data-im-cart-fulfillment-detail]');
      if (detail) detail.textContent = label(b, 'rc-delivery-passed', HD_PASSED);
    });
    var title = label(list[0], 'home-delivery', 'Home delivery');
    Array.prototype.slice
      .call(document.querySelectorAll('[data-im-cart-fulfillment-line]'))
      .forEach(function (el) {
        if (el.classList.contains('im-cart-fulfillment--delivery')) {
          var text = el.querySelector('span:not(.im-cart-fulfillment__icon)');
          if (text) text.textContent = title;
        } else if (el.classList.contains('im-cart-fulfillment--note')) {
          el.textContent = words;
        }
      });
  }

  /*
   * Ask the cart's Best Buy SKUs in order until one answers with a window. Stops
   * on any answer that is not "this SKU is not home delivery" — busy, an error,
   * the switch off, no window — because asking the next SKU would not change it.
   */
  function firstWindow(ask, skus, zip) {
    var i = 0;
    var asked = 0;
    function next() {
      if (i >= skus.length || asked >= HD_MAX_ASKS) return Promise.resolve(null);
      return ask(skus[i++], zip).then(function (r) {
        if (r.asked !== false) asked++;
        if (r.window) return r;
        if (r.error === 'not-home-delivery' || r.error === 'invalid-sku') return next();
        return null;
      });
    }
    return next().catch(function () { return null; });
  }

  /*
   * Write the new window, and have Hyper re-render both cart surfaces from it.
   *
   * ALL ELEVEN choice keys, as every writer does, with the window marked "as soon
   * as" — plus the two display-only pickup keys blank (2026-10-17) and the budget
   * stamp. The re-render is the theme's own pattern for an
   * attribute write (gift-wrapping.js): collect every cart section on the page
   * with `cart:grouped-sections`, send them with the update, publish `cartUpdate`
   * with the answer. The cart page and the drawer then re-render from Liquid, so
   * the new date is worded by the one formatter
   * (improvemint-delivery-window-label.liquid) rather than by a copy here. Where
   * that machinery is missing, the banners are repainted in place from the label
   * soonestWindow() returned, and Liquid catches up on the next render.
   */
  function writeWindow(banner, win, zip, today, text) {
    var attributes = {
      bb_fulfillment: 'home-delivery',
      bb_zip: banner.getAttribute('data-im-cart-zip') || zip,
      bb_store_id: '',
      bb_store_name: '',
      bb_shipping_option: '',
      bb_delivery_date: win.date,
      bb_delivery_start: win.startTime,
      bb_delivery_end: win.endTime || '',
      bb_delivery_zip: zip,
      bb_delivery_earliest: '1',
      bb_delivery_services: '',
      bb_pickup_ready_date: '',
      bb_store_address: '',
      bb_delivery_checked: today
    };
    var events = window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS;
    var sections = [];
    if (events) {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
      );
    }
    var body = { attributes: attributes };
    if (sections.length) body.sections = sections;

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (state) {
        if (!state) return;
        /*
         * The PDP picker's view of the cart, when this drawer was opened on a
         * product page — the same reason clearEmptyCart() updates it. Left with
         * the old date, a later Home Delivery click there would re-assert the old
         * window over this one (selectHomeDelivery → commitWindow).
         */
        var picker = document.querySelector('[data-im-fulfillment]');
        if (picker && picker.getAttribute('data-im-cart-fulfillment') === 'home-delivery') {
          picker.setAttribute('data-im-cart-delivery-date', attributes.bb_delivery_date);
          picker.setAttribute('data-im-cart-delivery-start', attributes.bb_delivery_start);
          picker.setAttribute('data-im-cart-delivery-end', attributes.bb_delivery_end);
          picker.setAttribute('data-im-cart-delivery-zip', zip);
          picker.setAttribute('data-im-cart-delivery-earliest', '1');
        }
        if (events && state.sections) {
          FoxTheme.pubsub.publish(events.cartUpdate, { cart: state });
          return;
        }
        banners().forEach(function (b) { paintState(b, 'home-delivery', text || ''); });
      })
      .catch(function () { /* non-fatal: the next look re-reads it */ });
  }

  /*
   * Per PAGE, like settledKey: the cart page renders the page's banner and the
   * drawer's, and both must share one re-read. Keyed by the window, so our own
   * write — which re-renders a banner carrying today's stamp — closes the question
   * rather than re-opening it.
   */
  var hdPending = false;
  var hdSettledKey = null;

  function refreshDelivery() {
    var list = banners();
    if (!list.length) return;
    var win = cartWindowOf(list[0]);
    if (!win) return;
    var today = todayUTC();
    if (!windowNeedsReading(win, today)) return;

    var key = windowKey(win);
    if (hdPending || hdSettledKey === key || retryHeld(key)) return;
    var ask = window.imDelivery && window.imDelivery.soonestWindow;
    if (!ask) return;                        // the delivery script never loaded

    // Tagged lines first, each group in cart order — see WHICH SKU above. Two
    // filters rather than a sort, so the order inside each group is the cart's
    // on every engine.
    var seen = {};
    var all = lines(list[0]);
    var skus = all
      .filter(function (l) { return l.home; })
      .concat(all.filter(function (l) { return !l.home; }))
      .map(function (l) { return l.sku; })
      .filter(function (s) { if (seen[s]) return false; seen[s] = true; return true; });

    hdPending = true;
    firstWindow(ask, skus, win.zip).then(function (found) {
      hdPending = false;
      hdSettledKey = key;
      // A passed date was already taken off the screen by run(), and stays off.
      if (!found) { holdRetry(key); return; }
      writeWindow(list[0], found.window, win.zip, today, found.label);
    });
  }

  /* ---------------------------------------------------------------- wiring */

  function run() {
    // Before anything else, and before the `no banner` return below swallows
    // it: an empty cart renders the stale marker and no banner at all.
    if (clearEmptyCart()) return;

    var list = banners();
    if (!list.length) return;

    var items = lines(list[0]);
    if (!items.length) return;

    var key = skuKey(items);
    var memo = memoGet();
    // A remembered message describes the cart it was raised for. An add or a
    // remove makes it somebody else's finding.
    if (memo && memo.key !== key) { memoClear(); memo = null; }

    if (list[0].getAttribute('data-state') === 'pickup') {
      /*
       * The shopper has chosen pickup since we last spoke, so whatever we said
       * before is superseded by their own act. Clear it and let the check below
       * answer afresh — a stale "switched to ground shipping" over a banner
       * reading "Store pickup" is the worst of both.
       */
      memoClear();
      list.forEach(function (b) { paintNotice(b, null); });
      list.forEach(watch);
      return;
    }

    // Not a pickup cart: nothing to check, and NO network. Just re-show a message
    // we raised earlier, so our own cart write does not erase its own reason.
    list.forEach(function (b) { paintNotice(b, memo); });
    if (memo) paintLines(memo);

    /*
     * A home-delivery cart. A PASSED date comes off the screen now, on every
     * render, with no request — Liquid prints it again each time. The re-read
     * itself waits, like the pickup check, until the shopper looks at the cart.
     */
    var win = cartWindowOf(list[0]);
    if (win) {
      if (win.date < todayUTC()) paintPassedWindow();
      list.forEach(function (b) { watch(b); });
    }
  }

  /*
   * A PDP write moved the choice while cart markup was already on the page.
   *
   * The mini-cart is global, so its Liquid was rendered before the shopper
   * touched the picker, and Hyper only re-renders the cart section on an add or a
   * quantity change — not on a bare `/cart/update.js`. Both pickers therefore
   * announce what they wrote, and this repaints from it rather than re-fetching
   * anything. Liquid stays the source of truth on the next render; this only
   * stops the banner disagreeing with the radio the shopper is looking at.
   */
  document.addEventListener('im:cart-fulfillment-change', function (e) {
    var detail = e.detail || {};
    var state = detail.state === 'store-pickup'
      ? 'pickup'
      : detail.state === 'home-delivery' ? 'home-delivery' : 'shipping';
    // The shopper re-chose, so any downgrade we raised is no longer the story.
    memoClear();
    settledKey = null;
    settledVerdict = null;
    banners().forEach(function (b) {
      paintState(b, state, detail.value || '');
      paintNotice(b, null);
      if (state === 'pickup') watch(b, true);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Theme-editor edits and section reloads.
  document.addEventListener('shopify:section:load', run);

  /*
   * The cart page and the drawer replace their bodies with freshly-rendered HTML
   * on every quantity change and remove (`CartItems.onCartUpdate` in cart.js
   * swaps innerHTML, which fires no `shopify:section:load`). So a new banner can
   * appear at any time, carrying Liquid's state and no notice.
   *
   * childList only, and filtered to banners: painting a banner rewrites text
   * inside it, which is also a childList mutation, but the nodes we add are Text
   * and <p>/<strong> that carry no [data-im-cart-fulfillment-banner] — so this
   * ignores our own writes and cannot loop. run() is idempotent and the verdict
   * is cached, so a burst of insertions costs one debounced pass and no network.
   *
   * The stale marker is watched for too, and it is the case that matters most
   * here: removing the LAST line re-renders the cart body with no banner at all,
   * only the marker, so watching banners alone would miss the very render that
   * says the cart is now empty.
   */
  var WATCHED = '[data-im-cart-fulfillment-banner], [data-im-cart-fulfillment-stale]';

  if (window.MutationObserver) {
    var pending = null;
    new MutationObserver(function (records) {
      var found = false;
      for (var i = 0; i < records.length && !found; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches(WATCHED) || n.querySelector(WATCHED)) {
            found = true;
            break;
          }
        }
      }
      if (!found || pending) return;
      pending = setTimeout(function () {
        pending = null;
        run();
      }, 50);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
